import api from '@/utils/axios';
import { unwrapItem, unwrapList } from '@/utils/apiHelpers';

export interface DriverDashboardStats {
  today_earnings: number;
  week_earnings: number;
  month_earnings: number;
  total_earnings: number;
  today_deliveries: number;
  week_deliveries: number;
  month_deliveries: number;
  total_deliveries: number;
  completed_deliveries: number;
  cancelled_deliveries: number;
  rejected_deliveries: number;
  pending_missions: number;
  active_missions: number;
  average_rating: number;
  acceptance_rate: number;
  completion_rate: number;
  distance_km_today: number;
  coverage_zones_count: number;
  insurance_expiry: string | null;
  active_trips?: number;
}

export interface DriverMissionSummary {
  id: number;
  code: string;
  status: string;
  package_description: string;
  delivery_type?: string | null;
  price: number;
  created_at: string;
  departure_zone?: string | null;
  arrival_zone?: string | null;
  departure_address?: string;
  arrival_address?: string;
  client_name?: string | null;
  client_phone?: string | null;
}

export interface DriverTrip {
  id: number;
  driver_id: number;
  status: string;
  from: { city: string };
  to: { city: string };
  departure_date: string;
  available_space: number;
  price_per_kg: number;
}

export interface DriverDashboardData {
  driver_name: string;
  driver_type: 'community' | 'professional';
  stats: DriverDashboardStats;
  pending_missions: DriverMissionSummary[];
  active_missions: DriverMissionSummary[];
  trips?: DriverTrip[];
}

export interface DriverWalletTransaction {
  id: string;
  type: string;
  amount: number;
  status: string;
  provider?: string | null;
  reference?: string | null;
  created_at: string;
}

export interface DriverWalletData {
  balance: number;
  pending_amount: number;
  today_earnings: number;
  week_earnings: number;
  month_earnings: number;
  total_earnings: number;
  today_deliveries: number;
  week_deliveries: number;
  month_deliveries: number;
  total_deliveries: number;
  average_rating: number;
  completion_rate: number;
  pending_withdrawals: DriverWalletTransaction[];
  transactions: DriverWalletTransaction[];
}

export interface DriverRouteStop {
  id: number;
  type: 'start' | 'pickup' | 'dropoff';
  label: string;
  address: string;
  latitude: number | null;
  longitude: number | null;
  order: DriverMissionSummary | null;
}

export interface DriverRouteData {
  total_distance_km: number;
  total_duration_min: number;
  stops_count: number;
  stops: DriverRouteStop[];
  maps_url: string | null;
}

export interface DriverZonePerformance {
  id: number;
  name: string;
  deliveries: number;
  revenue: number;
  rating: number;
  is_active: boolean;
}

export interface DriverZonesData {
  active_zones: DriverZonePerformance[];
  available_zones: Array<{ id: number; name: string }>;
  performance: DriverZonePerformance[];
}

export interface DriverInsuranceData {
  is_active: boolean;
  expiry_date: string | null;
  document_id: number | null;
  coverage: {
    vehicle: boolean;
    goods: boolean;
    accidents: boolean;
    max_coverage: number;
  };
  claims: Array<{
    id: string;
    date: string;
    type: string;
    status: string;
    amount: number;
  }>;
  emergency: {
    phone: string;
    label: string;
  };
}

export interface WalletOperationPayload {
  amount: number;
  payer_phone: string;
  mobile_money_provider: 'orange' | 'mtn';
}

export interface CommunityTripPayload {
  departure_city: string;
  arrival_city: string;
  departure_zone_id?: number | null;
  arrival_zone_id?: number | null;
  departure_date: string;
  available_space: number;
  price_per_kg: number;
  status?: string;
}

export async function fetchDriverDashboard(): Promise<DriverDashboardData> {
  const res = await api.get('/driver/dashboard');
  return unwrapItem<DriverDashboardData>(res.data);
}

export async function fetchDriverStats(): Promise<DriverDashboardStats> {
  const res = await api.get('/driver/stats');
  return unwrapItem<DriverDashboardStats>(res.data);
}

export async function fetchDriverWallet(): Promise<DriverWalletData> {
  const res = await api.get('/driver/wallet');
  return unwrapItem<DriverWalletData>(res.data);
}

export async function withdrawDriverWallet(payload: WalletOperationPayload): Promise<DriverWalletData> {
  const res = await api.post('/driver/wallet/withdraw', payload);
  return unwrapItem<DriverWalletData>(res.data);
}

export async function fetchDriverRoute(): Promise<DriverRouteData> {
  const res = await api.get('/driver/route');
  return unwrapItem<DriverRouteData>(res.data);
}

export async function fetchDriverZones(): Promise<DriverZonesData> {
  const res = await api.get('/driver/zones');
  return unwrapItem<DriverZonesData>(res.data);
}

export async function fetchDriverInsurance(): Promise<DriverInsuranceData> {
  const res = await api.get('/driver/insurance');
  return unwrapItem<DriverInsuranceData>(res.data);
}

export async function fetchDriverTrips(): Promise<DriverTrip[]> {
  const res = await api.get('/driver/trips');
  return unwrapList<DriverTrip>(res.data);
}

export async function createDriverTrip(payload: CommunityTripPayload): Promise<DriverTrip> {
  const res = await api.post('/driver/trips', payload);
  return unwrapItem<DriverTrip>(res.data);
}

export async function updateDriverTrip(id: number, payload: Partial<CommunityTripPayload>): Promise<DriverTrip> {
  const res = await api.patch(`/driver/trips/${id}`, payload);
  return unwrapItem<DriverTrip>(res.data);
}

export async function deleteDriverTrip(id: number): Promise<void> {
  await api.delete(`/driver/trips/${id}`);
}

export async function listMyDriverOrderRequests(status?: string) {
  const res = await api.get('/drivers/me/order-requests', {
    params: status ? { status } : undefined,
  });
  return unwrapList(res.data);
}
